import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API_BASE, apiError } from "@/lib/api";
import { ShieldCheck, Lock, Loader2 } from "lucide-react";

const money = (sym, v) => `${sym || "€"}${Number(v || 0).toFixed(2)}`;

export default function InvoicePay() {
  const { iid } = useParams();
  const [paying, setPaying] = useState(false);
  const [err, setErr] = useState(null);

  const [inv, setInv] = useState(undefined); // undefined=loading, null=not found
  useEffect(() => {
    axios.get(`${API_BASE}/public/pay/${iid}`)
      .then((r) => setInv(r.data))
      .catch(() => setInv(null));
  }, [iid]);

  const pay = async () => {
    setPaying(true); setErr(null);
    try {
      const { data } = await axios.post(`${API_BASE}/public/pay/${iid}/checkout`, { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      setErr(apiError(e.response?.data?.detail));
      setPaying(false);
    }
  };

  if (inv === undefined) {
    return <Screen><Loader2 className="animate-spin text-blue-400" size={28} /></Screen>;
  }
  if (inv === null) {
    return <Screen><p className="text-slate-300" data-testid="pay-not-found">Sorry, this invoice link is not valid.</p></Screen>;
  }

  const sym = inv.currency || "€";
  const settled = (inv.balance || 0) <= 0.005;

  return (
    <Screen>
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <p className="text-blue-300 text-xs font-semibold tracking-widest uppercase">{inv.business_name || "Amden One"}</p>
          <h1 className="mt-2 text-2xl font-bold text-white">Invoice {inv.number}</h1>
          <p className="text-slate-400 text-sm mt-1">Billed to {inv.customer_name}</p>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden" data-testid="pay-card">
          <div className="p-6">
            <div className="space-y-2">
              {(inv.line_items || []).map((li, i) => (
                <div key={i} className="flex justify-between text-sm">
                  <span className="text-slate-600">{li.description || "Item"} {li.quantity > 1 ? `× ${li.quantity}` : ""}</span>
                  <span className="text-slate-800 font-medium">{money(sym, (li.quantity || 1) * (li.unit_price || 0))}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-slate-100 flex justify-between text-sm">
              <span className="text-slate-500">Invoice total</span>
              <span className="text-slate-700 font-medium">{money(sym, inv.total)}</span>
            </div>
            {inv.amount_paid > 0 && (
              <div className="flex justify-between text-sm mt-1">
                <span className="text-slate-500">Already paid</span>
                <span className="text-emerald-600 font-medium">−{money(sym, inv.amount_paid)}</span>
              </div>
            )}
            <div className="mt-3 pt-3 border-t border-slate-100 flex justify-between items-center">
              <span className="text-slate-900 font-semibold">Amount due</span>
              <span className="text-2xl font-bold text-navy" data-testid="pay-amount-due">{money(sym, inv.balance)}</span>
            </div>
          </div>

          <div className="px-6 pb-6">
            {settled ? (
              <div className="rounded-xl bg-emerald-50 text-emerald-700 text-sm font-medium px-4 py-3 text-center" data-testid="pay-settled">
                This invoice has been paid in full. Thank you!
              </div>
            ) : (
              <>
                {err && <p className="text-red-600 text-sm mb-3 text-center" data-testid="pay-error">{err}</p>}
                <button onClick={pay} disabled={paying} data-testid="pay-now-button"
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold shadow-lg transition-colors">
                  {paying ? <Loader2 size={18} className="animate-spin" /> : <Lock size={16} />}
                  {paying ? "Redirecting…" : `Pay ${money(sym, inv.balance)}`}
                </button>
                <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-slate-400">
                  <ShieldCheck size={13} /> Secure payment powered by Stripe
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </Screen>
  );
}

const Screen = ({ children }) => (
  <div className="min-h-screen bg-navy grain-navy flex items-center justify-center px-4 py-12">{children}</div>
);
